import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import HomePage from '../Pages/Shared/HomePage';
import LinkedPoliciesPage from '../Pages/PolicyHolder/LinkedPoliciesPage';
import PolicyDetailsPage from '../Pages/PolicyHolder/PolicyDetailsPage';
import AgentDetailsPage from '../Pages/Agent/AgentDetailsPage';
import TerminatedAgentDetailsPage from '../Pages/Agent/TerminatedAgentDetailsPage';
import AdminPage from '../Pages/Admin/AdminPage';
import ICoverLoginPage from '../Pages/ICover/ICoverLoginPage';
import ICoverTwoFactorPage from '../Pages/ICover/ICoverTwoFactorPage';
import LoginPage from '../Pages/Account/LoginPage';
import TimeSystemLoginPage from '../Pages/Account/TimeSystemLoginPage';
import TwoFactorPage from '../Pages/Account/TwoFactorPage';
import ForgotUsernamePage from '../Pages/Account/ForgotUsernamePage';
import ForgotPasswordPage from '../Pages/Account/ForgotPasswordPage';
import ResetPasswordPage from '../Pages/Account/ResetPasswordPage';
import RegisterPage from '../Pages/Account/RegisterPage';
import RegisterEntityPage from '../Pages/Account/RegisterEntityPage';
import RegisterAgentPage from '../Pages/Account/RegisterAgentPage';
import ErrorPage from '../Pages/Shared/ErrorPage';
import UnfoundPage from '../Pages/Shared/UnfoundPage';
import NavBar from '../Components/NavBar/NavBar';
import ImageViewerSection from '../Components/Image/ImageViewerSection';
import UnderwritingSection from '../Components/Underwriting/UnderwritingSection';
import ProviderPage from '../Components/Provider/ProviderPage';
import RetentionLimitPage from '../Pages/Underwriting/RetentionLimitPage';
import ProtectedRoute from "./components/ProtectedRoute";
import ProfilePage from '../Pages/Shared/ProfilePage';
import Footer from '../Components/Footer/Footer';
import './App.css';
import { useAuth } from './Context/AuthContext';

const App = () => {
    const [portalSettings, setPortalSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const { logout } = useAuth();

    // ENV Variables
    const apiURL = import.meta.env.VITE_API;

    // Fetch portal settings once on app load
    useEffect(() => {
        const fetchPortalSettings = async () => {
            try {
                const res = await fetch(`${apiURL}/shared/getPortalSettings`, {
                    method: "GET"
                });

                if (res.status === 401) {
                    logout();
                    navigate('/login');
                    throw new Error("unauthorized");
                }

                if (res.status === 403) {
                    navigate('/home');
                    throw new Error("forbidden");
                }

                if (!res.ok) {
                    throw new Error("Failed to fetch portal settings");
                } 

                const data = await res.json();
                setPortalSettings(data);
            } catch (err) {
                console.error("Error fetching portal settings:", err);
            } finally {
                setSettingsLoading(false);
            }
        };
        fetchPortalSettings();
    }, []);

    //Set Variable CSS Colors
    useEffect(() => {
        if (portalSettings) {
            const root = document.documentElement;
            if (portalSettings.primaryColor)
                root.style.setProperty('--primary-color', portalSettings.primaryColor);
            if (portalSettings.secondaryColor)
                root.style.setProperty('--secondary-color', portalSettings.secondaryColor);
            if (portalSettings.alternateColor)
                root.style.setProperty('--alternate-color', portalSettings.alternateColor);
            if (portalSettings.electiveColor)
                root.style.setProperty('--elective-color', portalSettings.electiveColor);
            if (portalSettings.textPrimary)
                root.style.setProperty('--text-primary', portalSettings.textPrimary);
            if (portalSettings.textSecondary)
                root.style.setProperty('--text-secondary', portalSettings.textSecondary);
        }
    }, [portalSettings]);


    // Show loader until portal settings are loaded
    if (settingsLoading) return <div>Loading...</div>;

    return (
        <>
            {/* Pass portalSettings to Navbar so it can hide/show links */}
            <NavBar portalSettings={portalSettings} />
            <Routes>
                <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                <Route path="/home" element={<ProtectedRoute><HomePage portalSettings={portalSettings} /></ProtectedRoute>} />

                {/* Admin Routes */}
                <Route path="/admin" element={<ProtectedRoute><AdminPage portalSettings={portalSettings} /></ProtectedRoute>} />
                <Route path="/amradmin" element={<ProtectedRoute><AdminPage portalSettings={portalSettings} /></ProtectedRoute>} />

                {/* Policy / Agent Routes */}
                <Route
                    path="/policy/:polNo"
                    element={
                        <ProtectedRoute isEnabled={portalSettings.hasPolicyPortal}>
                            <PolicyDetailsPage portalSettings={portalSettings} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/agent/:agtCode"
                    element={
                        <ProtectedRoute isEnabled = {portalSettings.hasAgentPortal}>
                            <AgentDetailsPage portalSettings={portalSettings} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/TermAgent/:agtCode"
                    element={
                        <ProtectedRoute isEnabled={portalSettings.hasAgentPortal}>
                            <TerminatedAgentDetailsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/policies"
                    element={
                        <ProtectedRoute isEnabled={portalSettings.hasPolicyPortal}>
                            <LinkedPoliciesPage portalSettings={portalSettings} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <ProfilePage />
                        </ProtectedRoute>
                    }
                />

                {/* Image Portal */}
                <Route
                    path="/images"
                    element={
                        <ProtectedRoute isEnabled={portalSettings.hasImagePortal}>
                            <ImageViewerSection />
                        </ProtectedRoute>
                    }
                />
                
                {/* Underwriting Portal */}
                <Route
                    path="/underwriting"
                    element={
                        <ProtectedRoute isEnabled={portalSettings.hasUnderwritingPortal}>
                            <UnderwritingSection portalSettings={portalSettings} />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/RetentionLimit"
                    element={
                        <ProtectedRoute isEnabled={portalSettings.hasUnderwritingPortal}>
                            <RetentionLimitPage />
                        </ProtectedRoute>
                    }
                />

                {/* Provider Portal */}
                
                {portalSettings.hasProviderPortal && (
                    <Route
                        path="/provider"
                        element={
                                <ProviderPage />
                        }
                    />
                )}

                {/* ICover SSO Links */ }
                {portalSettings.hasICover && (
                    <Route path="/icoverlogin" element={<ICoverLoginPage portalSettings={portalSettings} />} />
                )}
                {portalSettings.hasICover && (
                    <Route path="/icovertwofactor" element={<ICoverTwoFactorPage portalSettings={portalSettings} />} />
                )}

                {/* Auth & Registration */}
                <Route path="/login" element={<LoginPage portalSettings={portalSettings} />} />
                <Route path="/timesystemlogin" element={<TimeSystemLoginPage portalSettings={portalSettings} />} />
                <Route path="/twofactor" element={<TwoFactorPage portalSettings={portalSettings} />} />
                <Route path="/forgotusername" element={<ForgotUsernamePage portalSettings={portalSettings} />} />
                <Route path="/forgotpassword" element={<ForgotPasswordPage portalSettings={portalSettings} />} />
                <Route path="/resetpassword" element={<ResetPasswordPage portalSettings={portalSettings} />} />
                <Route path="/register" element={<RegisterPage portalSettings={portalSettings} />} />
                <Route path="/registerEntity" element={<RegisterEntityPage portalSettings={portalSettings} />} />
                <Route path="/registerAgent" element={<RegisterAgentPage portalSettings={portalSettings} />} />

                {/* Error / Fallback Pages */}
                <Route path="/error" element={<ErrorPage portalSettings={portalSettings} />} />
                <Route path="*" element={<UnfoundPage portalSettings={portalSettings} />} />
            </Routes>
            <Footer/>
        </>
    );
};

export default App;
